import { ToolPart } from '@cotera/sdk/core';
import type { InputHijacker, InputHijackerProps } from './types';

const HIJACKERS: InputHijacker[] = [];
let APPROVAL_HIJACKER: {
  component: React.ComponentType<InputHijackerProps>;
} | null = null;

/**
 * Register a tool renderer to hijack the input when that tool has an incomplete
 * tool call. The component will be shown inside the input box above the text
 * field.
 *
 * @param toolKey - Tool key to match (e.g. 'coco/user-options')
 * @param component - React component that receives part, chatModel, readonly, embedded
 * @param options.priority - When multiple tools could match, higher priority wins (default: 0)
 * @param options.inputPlaceholder - Placeholder text to show in the input field when this hijacker is active
 */
export function registerInputHijacker(
  toolKey: string,
  component: InputHijacker['component'],
  options?: {
    priority?: number;
    predicate?: (part: ToolPart) => boolean;
    inputPlaceholder?: string;
  }
): void {
  HIJACKERS.push({
    toolKey,
    component,
    priority: options?.priority ?? 0,
    predicate: options?.predicate ?? (() => true),
    inputPlaceholder: options?.inputPlaceholder,
  });
}

export function getInputHijackers(): InputHijacker[] {
  return [...HIJACKERS].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

/**
 * Register a special hijacker component that handles tool approvals.
 * This hijacker takes precedence over all other hijackers when a tool
 * has approvalStatus() === 'required'.
 *
 * @param component - React component that renders the approval UI
 */
export function registerApprovalHijacker(
  component: React.ComponentType<InputHijackerProps>
): void {
  APPROVAL_HIJACKER = { component };
}

/**
 * Get the registered approval hijacker, if any.
 */
export function getApprovalHijacker(): {
  component: React.ComponentType<InputHijackerProps>;
} | null {
  return APPROVAL_HIJACKER;
}
